import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ShieldCheck, MessageSquare, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('futurepath_token')}` } });

/**
 * Caseload-level NC CDP compliance rollup for counselors.
 * Highlights below-80% students and open parent review requests.
 */
export default function CDPCaseloadCompliance({ onOpenStudent }) {
  const [data, setData] = useState(null);
  const [reqs, setReqs] = useState(null);

  useEffect(() => {
    axios.get(`${API}/nc/cdp/counselor/compliance-rollup`, auth())
      .then((r) => setData(r.data)).catch(() => setData({ students: [] }));
    axios.get(`${API}/nc/cdp/counselor/review-requests`, auth())
      .then((r) => setReqs(r.data)).catch(() => setReqs({ requests: [], total: 0 }));
  }, []);

  if (!data || !data.students?.length) return null;

  const openReqCount = reqs?.total || 0;
  const belowStudents = data.students.filter((s) => s.score < data.threshold);
  const scoreHue = data.average >= 85 ? '#22d3ee' : data.average >= 70 ? '#a78bfa' : '#f472b6';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid="cdp-caseload-compliance"
      className="relative overflow-hidden rounded-2xl border border-cyan-500/25 bg-[#0a1729]/60"
      style={{ boxShadow: '0 30px 80px -60px rgba(34,211,238,0.5)' }}
    >
      <div aria-hidden className="pointer-events-none absolute -right-24 -top-20 h-56 w-56 rounded-full blur-3xl opacity-25"
        style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.35), transparent 65%)' }} />

      <div className="relative">
        <div className="flex flex-wrap items-center gap-3 border-b border-white/[0.06] px-6 py-4">
          <ShieldCheck className="h-4 w-4 text-cyan-300" />
          <h2 className="font-outfit text-base font-semibold text-white">
            NC CDP Compliance · § 115C-158.10
          </h2>
          <span className="rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider"
            style={{ borderColor: `${scoreHue}55`, background: `${scoreHue}15`, color: scoreHue }}>
            Caseload avg · {data.average}%
          </span>
          <span className="text-[11px] text-slate-300 ml-auto">
            {data.below_threshold} below {data.threshold}%
            {openReqCount > 0 && ` · ${openReqCount} parent review${openReqCount === 1 ? '' : 's'} pending`}
          </span>
        </div>

        {belowStudents.length > 0 ? (
          <div className="divide-y divide-white/[0.05]">
            {belowStudents.slice(0, 6).map((s) => (
              <button key={s.student_id}
                onClick={() => onOpenStudent && onOpenStudent(s.student_id)}
                data-testid={`compliance-row-${s.student_id}`}
                className="flex w-full items-center gap-3 px-6 py-3 text-left hover:bg-white/[0.04] transition-colors">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-cyan-300/25 bg-cyan-500/10 text-xs font-semibold text-cyan-100">
                  {(s.student_name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('')}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">
                    {s.student_name} <span className="text-slate-300">· Grade {s.grade || '-'}</span>
                  </p>
                  <p className="text-[11px] text-slate-300 truncate">
                    Gaps: {s.gaps.slice(0, 3).map((g) => g.replace(/_/g, ' ')).join(', ') || 'none'}
                    {s.open_parent_requests > 0 && (
                      <span className="ml-1 text-violet-300">
                        · <MessageSquare className="inline h-3 w-3" /> {s.open_parent_requests} parent request
                      </span>
                    )}
                  </p>
                </div>
                <span className="rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider"
                  style={{ borderColor: `${scoreHue}55`, background: `${scoreHue}15`, color: scoreHue }}>
                  {s.score}%
                </span>
                <ArrowRight className="h-4 w-4 text-slate-300" />
              </button>
            ))}
          </div>
        ) : (
          <div className="px-6 py-4 text-sm text-slate-300">
            Every student on your caseload is at or above the {data.threshold}% compliance threshold. 🎉
          </div>
        )}
      </div>
    </motion.div>
  );
}
